import React from 'react';
import { Target } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { AuditTable } from './AuditTable.jsx';
import { parseNumeric, resolveLiveRows, mergedColumns } from './columnClassification.js';
import { ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { hasUpstreamSplit, SOURCE_ROWS } from '../../data.js';

function getFitRows(rows) {
  if (!rows || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

function parseVal(v) {
  const n = parseNumeric(v);
  return n === null ? NaN : n;
}

function rankArray(arr) {
  const sorted = arr.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const ranks = new Array(arr.length);
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j < sorted.length && sorted[j].v === sorted[i].v) j++;
    const avgRank = (i + j - 1) / 2 + 1;
    for (let k = i; k < j; k++) {
      ranks[sorted[k].i] = avgRank;
    }
    i = j;
  }
  return ranks;
}

function computePearson(x, y) {
  const n = x.length;
  if (n < 2) return null;
  const mx = x.reduce((a, b) => a + b, 0) / n;
  const my = y.reduce((a, b) => a + b, 0) / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx;
    const dy = y[i] - my;
    num += dx * dy;
    dx2 += dx * dx;
    dy2 += dy * dy;
  }
  if (dx2 === 0 || dy2 === 0) return null;
  return num / Math.sqrt(dx2 * dy2);
}

function computeSpearman(x, y) {
  if (x.length < 2) return null;
  const rx = rankArray(x);
  const ry = rankArray(y);
  return computePearson(rx, ry);
}

function computeKendall(x, y) {
  const n = x.length;
  if (n < 2) return null;
  let concordant = 0, discordant = 0, tx = 0, ty = 0;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = x[i] - x[j];
      const dy = y[i] - y[j];
      if (dx === 0 && dy === 0) continue;
      if (dx === 0) tx++;
      else if (dy === 0) ty++;
      else if ((dx > 0 && dy > 0) || (dx < 0 && dy < 0)) concordant++;
      else discordant++;
    }
  }
  const denom = Math.sqrt((concordant + discordant + tx) * (concordant + discordant + ty));
  if (denom === 0) return null;
  return (concordant - discordant) / denom;
}

function computeCorrelationScores(rows, targetCol, mode, threshold, manualOverrides = {}, columnsList = []) {
  if (!rows || !rows.length || !targetCol) return [];
  const fitRows = getFitRows(rows);
  const typeOf = (name) => (columnsList.find(c => c.name === name)?.type) || 'string';

  // Correlation methods strictly require a numeric target variable. The
  // target dropdown already only offers columns whose declared schema type
  // is 'number' (see `targetCols` below), so that's the trusted signal here
  // too — not a re-derived majority-parse vote over this step's live rows.
  // A row-by-row majority vote used to gate the whole table: on a small
  // dataset (or one with a handful of blank/unparseable target cells in the
  // training partition), even a genuinely numeric target could tip under the
  // 50% threshold and incorrectly blank out every feature's score at once —
  // exactly the "target must be numeric" misfire reported for a real numeric
  // column. Per-row target values that fail to parse are still handled
  // correctly and individually further down (`xVals`/`yVals` only pair up
  // rows where both the feature and the target parse), so nothing about
  // genuinely messy target data goes unhandled by removing this pre-check.
  const targetMeta = columnsList.find(cl => cl.name === targetCol);
  const isTargetNumeric = targetMeta ? targetMeta.type === 'number' : true;

  if (!isTargetNumeric) {
    const candidateCols = columnsList.filter(c => c.name !== targetCol && c.name !== '__rowIndex' && c.type === 'number').map(c => c.name);
    return candidateCols.map(col => ({
      column: col,
      dataType: typeOf(col),
      score: null,
      metric: 'N/A (target must be numeric)',
      autoStatus: 'drop',
      override: manualOverrides[col] || 'auto'
    }));
  }

  // Find candidate numeric feature columns (excluding targetCol and __rowIndex)
  const candidateCols = columnsList.filter(c => {
    if (c.name === targetCol || c.name === '__rowIndex') return false;
    return c.type === 'number';
  }).map(c => c.name);

  return candidateCols.map(col => {
    // Collect valid pairs (x_i, y_i)
    const xVals = [];
    const yVals = [];
    let anyParseable = false;

    // Target is always numeric here — the `!isTargetNumeric` branch above
    // already returned before this loop runs, and the target dropdown only
    // ever offers numeric columns in the first place.
    for (const r of fitRows) {
      const x = parseVal(r[col]);
      if (isNaN(x)) continue;
      anyParseable = true;

      const y = parseVal(r[targetCol]);
      if (!isNaN(y)) {
        xVals.push(x);
        yVals.push(y);
      }
    }

    const dataType = typeOf(col);

    if (xVals.length < 2) {
      return {
        column: col,
        dataType,
        score: null,
        metric: anyParseable ? 'N/A (insufficient data)' : 'N/A (unparseable)',
        autoStatus: 'drop',
        override: manualOverrides[col] || 'auto'
      };
    }

    // Check if x is constant
    const isConstX = xVals.every(v => v === xVals[0]);
    if (isConstX) {
      return {
        column: col,
        dataType,
        score: null,
        metric: 'N/A (constant)',
        autoStatus: 'drop',
        override: manualOverrides[col] || 'auto'
      };
    }

    let score = null;
    if (mode === 'spearman') {
      score = computeSpearman(xVals, yVals);
    } else if (mode === 'kendall') {
      score = computeKendall(xVals, yVals);
    } else {
      score = computePearson(xVals, yVals);
    }

    if (score === null || isNaN(score)) {
      return {
        column: col,
        dataType,
        score: null,
        metric: 'N/A (zero spread)',
        autoStatus: 'drop',
        override: manualOverrides[col] || 'auto'
      };
    }

    const absScore = Math.abs(score);
    const autoStatus = absScore >= threshold ? 'keep' : 'drop';
    const sign = score > 0 ? '+' : '';

    return {
      column: col,
      dataType,
      score,
      absScore,
      metric: `${sign}${score.toFixed(4)}`,
      autoStatus,
      override: manualOverrides[col] || 'auto'
    };
  });
}

export function CorrelationInspector({ step, onChange, pipeline, uploadedRows }) {
  const modeMap = {
    pearson_correlation: { title: 'Pearson Correlation', symbol: 'r', mode: 'pearson', desc: 'Filter features by linear correlation coefficient with a target variable.' },
    spearman_correlation: { title: 'Spearman Correlation', symbol: 'ρ', mode: 'spearman', desc: 'Filter features by Spearman rank correlation coefficient with a target variable.' },
    kendall_correlation: { title: 'Kendall Tau Correlation', symbol: 'τ', mode: 'kendall', desc: 'Filter features by Kendall Tau ordinal association coefficient with a target variable.' }
  };

  const info = modeMap[step.type] || modeMap.pearson_correlation;
  const c = step.config || {};
  const targetCol = c.target || '';
  const threshold = c.threshold ?? 0.05;
  const manualOverrides = c.manual_overrides || {};

  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline, SOURCE_ROWS);

  const liveColumns = React.useContext(ColumnsContext) || [];
  const origColumns = React.useContext(OriginalColumnsContext) || [];
  const allCols = mergedColumns(origColumns, liveColumns);

  // Sorting state for live preview table
  const [sortKey, setSortKey] = React.useState('absScore');
  const [sortDir, setSortDir] = React.useState('desc');

  // Compute live scores — recomputed fresh on every render, so a threshold,
  // target, or column-selection change is reflected immediately.
  const scores = computeCorrelationScores(rows, targetCol, info.mode, threshold, manualOverrides, allCols);

  const setOverride = (col, next) => {
    const nextOverrides = { ...manualOverrides };
    if (next === 'auto') {
      delete nextOverrides[col];
    } else {
      nextOverrides[col] = next;
    }
    u({ manual_overrides: nextOverrides });
  };

  const sortedScores = React.useMemo(() => {
    return [...scores].sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];
      if (valA === null || valA === undefined) valA = -999;
      if (valB === null || valB === undefined) valB = -999;
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [scores, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const targetCols = React.useMemo(() => {
    return allCols.filter(col => col.type === 'number');
  }, [allCols]);

  return (
    <div>
      <Eyebrow>{info.title}</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        {info.desc}
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — correlations are calculated using all rows. Add a Train / Test Split step first to avoid data leakage.
        </Alert>
      )}

      {/* Target Variable Dropdown */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Select a numeric target variable to correlate features against">
          Target Variable (Numeric Only)
        </FieldLabel>
        <select
          value={targetCol}
          onChange={(e) => u({ target: e.target.value })}
          style={{
            width: '100%', background: C.input, color: C.fg1,
            border: `1px solid ${C.borderStrong}`, borderRadius: 6,
            padding: '6px 9px', fontSize: '.82rem', outline: 'none',
            fontFamily: 'inherit', marginTop: 4, cursor: 'pointer'
          }}
        >
          <option value="" disabled>Select target column…</option>
          {targetCols.map(col => (
            <option key={col.name} value={col.name}>
              {col.name} ({col.type || 'string'})
            </option>
          ))}
        </select>
      </div>

      {/* Threshold Presets */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Quick threshold selection presets">Threshold Presets</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: `Weak (|${info.symbol}| > 0.01)`, val: 0.01 },
            { label: `Standard (|${info.symbol}| > 0.05)`, val: 0.05 },
            { label: `Strong (|${info.symbol}| > 0.10)`, val: 0.10 },
          ].map(p => {
            const active = threshold === p.val;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => u({ threshold: p.val })}
                style={{
                  background: active ? C.accentStrong : C.input,
                  color: active ? '#fff' : C.fg3,
                  border: `1px solid ${active ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '4px 8px', fontSize: '.74rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s'
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Threshold Input */}
      <div style={{ marginBottom: 14 }}>
        <FieldLabel hint={`Minimum absolute correlation threshold (|${info.symbol}|)`}>
          Minimum Correlation Threshold (|{info.symbol}|): {threshold}
        </FieldLabel>
        <TextInput
          type="number"
          step="0.01"
          min="0"
          max="1.0"
          value={threshold}
          onChange={(v) => u({ threshold: parseFloat(v) || 0.0 })}
          style={{ width: 120 }}
        />
      </div>

      {/* Target Context Header & Live Preview Table */}
      {targetCol ? (
        <>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <span style={{ fontSize: '.72rem', color: C.fg4 }}>Sort by:</span>
            {[{ key: 'column', label: 'Column' }, { key: 'absScore', label: `|${info.symbol}|` }].map(s => (
              <button
                key={s.key}
                type="button"
                onClick={() => handleSort(s.key)}
                style={{
                  background: sortKey === s.key ? C.accentStrong : C.input,
                  color: sortKey === s.key ? '#fff' : C.fg3,
                  border: `1px solid ${sortKey === s.key ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '3px 8px', fontSize: '.72rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                {s.label} {sortKey === s.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
            ))}
          </div>
          <AuditTable
            headerLabel={<><Target size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 5 }} />Evaluating features against Target: {targetCol}</>}
            rows={sortedScores.map(row => ({
              column: row.column,
              dataType: row.dataType,
              metric: row.metric,
              autoStatus: row.autoStatus,
              override: row.override,
              onOverrideChange: (next) => setOverride(row.column, next),
            }))}
            emptyMessage="No numeric candidate features found in dataset schema."
          />
        </>
      ) : (
        <div style={{
          marginTop: 10, padding: 12, borderRadius: 8, background: C.surface,
          border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
        }}>
          Please select a target variable above to display live feature correlation scores.
        </div>
      )}
    </div>
  );
}

